import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import cn from 'classnames'
import {
    ArrowLeft,
    BadgeCheck,
    Check,
    Eye,
    EyeOff,
    KeyRound,
    MailCheck,
    Pencil,
    RefreshCw,
    ShieldCheck,
} from 'lucide-react'
import { recoveryRequest, verifyRecoveryWallet, RecoveryGrant } from 'lib/wallet-recovery'
import styles from './auth.module.scss'

interface Props {
    onBack: () => void
    onRecovered: (response: Response, user: unknown) => void
}

const STEPS = ['Wallet', 'Email', 'Password']

function stepCopy(step: number) {
    switch (step) {
        case 0:
            return {
                title: 'Welcome back',
                lede: 'Prove you hold the Yours wallet you signed up with, then add an email and password so you can log in anywhere.',
            }
        case 1:
            return {
                title: 'Set up your login',
                lede: 'Everything on your account stays exactly where it is. Add an email we can reach you at.',
            }
        default:
            return {
                title: 'Confirm and choose a password',
                lede: 'Enter the code we emailed you, then pick a password for signing in.',
            }
    }
}

export default function WalletRecovery({ onBack, onRecovered }: Props) {
    // Recovery authorization stays in memory and never becomes an ordinary login token.
    const [grant, setGrant] = useState<(RecoveryGrant & { name: string }) | null>(null)
    const [email, setEmail] = useState('')
    const [sentEmail, setSentEmail] = useState('')
    const [code, setCode] = useState('')
    const [password, setPassword] = useState('')
    const [confirmation, setConfirmation] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const step = !grant ? 0 : !sentEmail ? 1 : 2
    const { title, lede } = stepCopy(step)

    async function run(action: () => Promise<void>) {
        if (busy) return
        setBusy(true)
        setError('')
        try {
            await action()
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Recovery could not continue.')
        } finally {
            setBusy(false)
        }
    }

    async function sendCode() {
        if (!grant) return
        await recoveryRequest(`/${grant.id}/email`, { email }, grant)
        setSentEmail(email.trim().toLowerCase())
        setCode('')
    }

    async function complete() {
        if (!grant) return
        const { response, data } = await recoveryRequest(
            `/${grant.id}/complete`,
            { email_code: code, password, password_confirmation: confirmation },
            grant,
        )
        setPassword('')
        setConfirmation('')
        onRecovered(response, data.user)
    }

    const spinner = <span className={styles.recoverySpinner} aria-hidden="true" />

    return (
        <section className={styles.walletRecovery} aria-label="Wallet account recovery">
            <button type="button" className={styles.recoveryBack} disabled={busy} onClick={onBack}>
                <ArrowLeft size={15} aria-hidden="true" />
                Back to login
            </button>

            <header className={styles.recoveryHeader}>
                <div className={styles.recoveryMark}>
                    <img src="/yours512.png" alt="" className={styles.recoveryMarkLogo} />
                    {grant && (
                        <span className={styles.recoveryMarkBadge} aria-hidden="true">
                            <Check size={12} strokeWidth={3} />
                        </span>
                    )}
                </div>
                <p className={styles.recoveryEyebrow}>Account recovery</p>
                <h2>{title}</h2>
                <p className={styles.recoveryLede}>{lede}</p>
            </header>

            <ol className={styles.recoverySteps} aria-label="Recovery progress">
                {STEPS.map((label, index) => (
                    <li
                        key={label}
                        className={cn(
                            styles.recoveryStep,
                            index < step && styles.recoveryStepDone,
                            index === step && styles.recoveryStepActive,
                        )}
                        aria-current={index === step ? 'step' : undefined}
                    >
                        <span className={styles.recoveryStepDot} aria-hidden="true">
                            {index < step ? <Check size={12} strokeWidth={3} /> : index + 1}
                        </span>
                        <span className={styles.recoveryStepLabel}>{label}</span>
                    </li>
                ))}
            </ol>

            <p role="alert" className={styles.recoveryAlert}>
                {error}
            </p>

            <div key={step} className={styles.recoveryPanel}>
                {!grant ? (
                    <>
                        <ul className={styles.recoveryAssurances}>
                            <li>
                                <span className={styles.recoveryAssuranceIcon}>
                                    <ShieldCheck size={16} aria-hidden="true" />
                                </span>
                                <span>
                                    Signing proves you own the wallet. It never moves funds.
                                </span>
                            </li>
                            <li>
                                <span className={styles.recoveryAssuranceIcon}>
                                    <KeyRound size={16} aria-hidden="true" />
                                </span>
                                <span>
                                    Never share your recovery phrase or private keys. Treechat will
                                    never ask for them.
                                </span>
                            </li>
                        </ul>
                        <button
                            type="button"
                            className={cn(styles.loginButton, styles.recoveryPrimary)}
                            disabled={busy}
                            onClick={() => run(async () => setGrant(await verifyRecoveryWallet()))}
                        >
                            {busy ? (
                                spinner
                            ) : (
                                <img
                                    src="/yours512.png"
                                    alt=""
                                    className={styles.recoveryButtonLogo}
                                />
                            )}
                            {busy
                                ? 'Approve the request in your wallet…'
                                : 'Verify with Yours Wallet'}
                        </button>
                        <p className={styles.recoveryAlt}>
                            Prefer email?{' '}
                            <Link to="/auth/password/request-reset" className={styles.recoveryLink}>
                                Recover using your existing email
                            </Link>
                        </p>
                    </>
                ) : (
                    <form
                        onSubmit={event => {
                            event.preventDefault()
                            run(sentEmail ? complete : sendCode)
                        }}
                        className={styles.authFields}
                    >
                        <p className={styles.recoveryVerified}>
                            <BadgeCheck size={16} aria-hidden="true" />
                            <span>
                                Account verified: <strong>@{grant.name}</strong>.
                            </span>
                        </p>
                        {!sentEmail ? (
                            <div className={styles.fieldContainer}>
                                <label
                                    htmlFor="wallet-recovery-email"
                                    className={styles.fieldLabel}
                                >
                                    Email
                                </label>
                                <input
                                    id="wallet-recovery-email"
                                    className={styles.inputField}
                                    type="email"
                                    autoComplete="email"
                                    autoFocus
                                    required
                                    maxLength={254}
                                    value={email}
                                    disabled={busy}
                                    onChange={event => setEmail(event.target.value)}
                                />
                                <p className={styles.recoveryHint}>
                                    We’ll send a six-digit code to confirm it’s yours.
                                </p>
                            </div>
                        ) : (
                            <>
                                <p role="status" className={styles.recoveryStatus}>
                                    <MailCheck size={16} aria-hidden="true" />
                                    <span>
                                        We sent a code to <strong>{sentEmail}</strong>. It expires
                                        in 10 minutes.
                                    </span>
                                </p>
                                <div className={styles.fieldContainer}>
                                    <label
                                        htmlFor="wallet-recovery-code"
                                        className={styles.fieldLabel}
                                    >
                                        Email code
                                    </label>
                                    <input
                                        id="wallet-recovery-code"
                                        className={cn(styles.inputField, styles.recoveryCodeInput)}
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        placeholder="000000"
                                        autoFocus
                                        required
                                        value={code}
                                        onChange={event => setCode(event.target.value)}
                                    />
                                    <div className={styles.recoveryActions}>
                                        <button
                                            type="button"
                                            className={styles.recoveryGhost}
                                            disabled={busy}
                                            onClick={() => run(sendCode)}
                                        >
                                            <RefreshCw size={13} aria-hidden="true" />
                                            Resend code
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.recoveryGhost}
                                            disabled={busy}
                                            onClick={() => {
                                                setSentEmail('')
                                                setCode('')
                                            }}
                                        >
                                            <Pencil size={13} aria-hidden="true" />
                                            Use a different email
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.fieldContainer}>
                                    <label
                                        htmlFor="wallet-recovery-password"
                                        className={styles.fieldLabel}
                                    >
                                        New password
                                    </label>
                                    <div className={styles.passwordField}>
                                        <input
                                            id="wallet-recovery-password"
                                            className={cn(styles.inputField, styles.passwordInput)}
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            minLength={8}
                                            required
                                            value={password}
                                            onChange={event => setPassword(event.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggle}
                                            onClick={() => setShowPassword(current => !current)}
                                            aria-label={
                                                showPassword ? 'Hide password' : 'Show password'
                                            }
                                        >
                                            {showPassword ? (
                                                <EyeOff size={16} />
                                            ) : (
                                                <Eye size={16} />
                                            )}
                                        </button>
                                    </div>
                                    <p className={styles.recoveryHint}>At least 8 characters.</p>
                                </div>
                                <div className={styles.fieldContainer}>
                                    <label
                                        htmlFor="wallet-recovery-confirmation"
                                        className={styles.fieldLabel}
                                    >
                                        Confirm password
                                    </label>
                                    <input
                                        id="wallet-recovery-confirmation"
                                        className={styles.inputField}
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        minLength={8}
                                        required
                                        value={confirmation}
                                        onChange={event => setConfirmation(event.target.value)}
                                    />
                                </div>
                            </>
                        )}
                        <button
                            type="submit"
                            className={cn(styles.loginButton, styles.recoveryPrimary)}
                            disabled={busy}
                        >
                            {busy && spinner}
                            {busy
                                ? 'Please wait…'
                                : sentEmail
                                ? 'Save login and sign in'
                                : 'Send verification code'}
                        </button>
                    </form>
                )}
            </div>
        </section>
    )
}
